import { ApiError } from '@/shared/api/api-error'
import { request } from '@/shared/api/request'
import { camelize } from '@/shared/lib/camelize'

/** Backend NotificationFlags - a [Flags] enum serialized as an Int bitmask. */
export const NOTIFICATION_FLAGS = {
  like: 1 << 0,
  comment: 1 << 1,
  follow: 1 << 2,
  newReferral: 1 << 3,
  reply: 1 << 4,
  mention: 1 << 5,
  giftReceived: 1 << 6,
  tokensEarned: 1 << 7,
  payoutReady: 1 << 8,
  push: 1 << 9,
  newPost: 1 << 10,
  message: 1 << 11,
} as const

export type NotificationFlag = keyof typeof NOTIFICATION_FLAGS

/** Backend default: everything on except Like. */
export const DEFAULT_NOTIFICATION_FLAGS = Object.entries(NOTIFICATION_FLAGS)
  .filter(([name]) => name !== 'like')
  .reduce((mask, [, bit]) => mask | bit, 0)

export function hasFlag(mask: number, flag: NotificationFlag): boolean {
  return (mask & NOTIFICATION_FLAGS[flag]) !== 0
}

export interface AppNotification {
  id: string
  type: number
  title: string
  body?: string | null
  actorId?: string | null
  actorName?: string | null
  actorAvatarUrl?: string | null
  date: string
  isRead: boolean
  data: Record<string, string>
}

export function notificationPostId(notification: AppNotification): string | null {
  return notification.data?.postId ?? notification.data?.post_id ?? null
}

export function notificationConversationId(notification: AppNotification): string | null {
  return notification.data?.conversationId ?? notification.data?.conversation_id ?? null
}

const BASE = '/api/notifications'

export const notificationsApi = {
  /** `days` limits results to the last N days (server-side filter). */
  async getNotifications(
    limit = 50,
    offset = 0,
    days = 30,
  ): Promise<{ notifications: AppNotification[]; unreadCount: number }> {
    const data = await request<unknown>({ url: BASE, params: { limit, offset, days } })
    const page = camelize<{ notifications?: AppNotification[]; unreadCount?: number }>(data)
    return { notifications: page.notifications ?? [], unreadCount: page.unreadCount ?? 0 }
  },

  async getUnreadCount(): Promise<number> {
    try {
      const data = await request<Record<string, unknown>>({ url: `${BASE}/unread-count` })
      const value = data?.count ?? data?.unread_count ?? data?.unreadCount
      return typeof value === 'number' ? value : 0
    } catch {
      return 0
    }
  },

  async markRead(id: string): Promise<void> {
    try {
      await request({ method: 'POST', url: `${BASE}/${id}/read` })
    } catch {
      /* best-effort */
    }
  },

  async markAllRead(): Promise<void> {
    try {
      await request({ method: 'POST', url: `${BASE}/read-all` })
    } catch {
      /* best-effort */
    }
  },

  async deleteNotification(id: string): Promise<void> {
    await request({ method: 'DELETE', url: `${BASE}/${id}` })
  },

  /** 404 means the profile has no settings row yet → backend defaults apply. */
  async getSettings(): Promise<number> {
    try {
      const data = await request<Record<string, unknown>>({ url: `${BASE}/settings` })
      return typeof data?.flags === 'number' ? data.flags : DEFAULT_NOTIFICATION_FLAGS
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return DEFAULT_NOTIFICATION_FLAGS
      throw error
    }
  },

  async updateSettings(input: { add?: number; remove?: number }): Promise<void> {
    const body: Record<string, number> = {}
    if (input.add) body.add = input.add
    if (input.remove) body.remove = input.remove
    if (Object.keys(body).length === 0) return
    await request({ method: 'PUT', url: `${BASE}/settings`, data: body })
  },
}
